import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { auctionService } from '../services/api';
import { AuctionDetail } from './AuctionDetailPage'; // Reuse interface or move to shared types

const AuctionBatchPage = () => {
    const { batchId } = useParams<{ batchId: string }>();
    const navigate = useNavigate();
    const [auctions, setAuctions] = useState<AuctionDetail[]>([]);
    const [loading, setLoading] = useState(true);
    const [, setError] = useState<string | null>(null);

    // Auth state
    const isAuthenticated = Boolean(localStorage.getItem('token'));
    const userStr = localStorage.getItem('user');
    // @ts-ignore
    let currentUser: any = null;
    if (userStr) {
        try { currentUser = JSON.parse(userStr); } catch (e) { console.error(e); }
    }

    // Inline bidding state map: { [auctionId]: bidAmount }
    const [bidAmounts, setBidAmounts] = useState<{ [key: number]: string }>({});
    const [placingBidOn, setPlacingBidOn] = useState<number | null>(null);

    const { t } = useTranslation();

    useEffect(() => {
        if (batchId) {
            fetchBatch();
        }
    }, [batchId]);

    const fetchBatch = async () => {
        try {
            setLoading(true);
            const response = await auctionService.getBatchAuctions(batchId!);
            setAuctions(response.data?.data || []);
        } catch (err) {
            console.error('Failed to fetch batch', err);
            setError(t('auctions.failedToLoadBatch'));
        } finally {
            setLoading(false);
        }
    };

    const handleCancelBatch = async () => {
        if (!window.confirm(t('auctions.cancelBatchConfirm'))) {
            return;
        }
        try {
            await auctionService.cancelBatch(batchId!);
            alert(t('auctions.batchCancelled'));
            fetchBatch(); // Refresh
        } catch (err: any) {
            alert(err?.response?.data?.message || t('auctions.failedToCancelBatch'));
        }
    };

    const handlePlaceBid = async (auction: AuctionDetail) => {
        const amount = bidAmounts[auction.id];
        if (!amount) return;

        if (!isAuthenticated) {
            navigate('/login');
            return;
        }

        try {
            setPlacingBidOn(auction.id);
            await auctionService.placeBid(String(auction.id), Number(amount));
            // Refresh just this item or whole batch? Whole batch is safer to sync everything
            fetchBatch();
            setBidAmounts(prev => ({ ...prev, [auction.id]: '' })); // Clear input
        } catch (err: any) {
            alert(err?.response?.data?.message || t('auctions.bidFailed'));
        } finally {
            setPlacingBidOn(null);
        }
    };

    const calculateMinBid = (auctionData: AuctionDetail) => {
        const highest = auctionData.bids?.reduce((max: number, bid: any) => Math.max(max, Number(bid.amount)), 0) || 0;
        if (highest > 0) {
            return highest + Number(auctionData.minIncrement || 0);
        }
        return Number(auctionData.startPrice || 0);
    };

    // Derived info from first auction (assuming homogeneity in seller/links for batch)
    const firstAuction = auctions[0];
    const seller = firstAuction?.seller;
    const facebookLink = firstAuction?.facebookLink;

    const getRarityColor = (rarity: string) => {
        const rarityLower = rarity.toLowerCase();
        if (rarityLower === 'c' || rarityLower === 'common') return 'bg-gray-500 text-white';
        if (rarityLower === 'r' || rarityLower === 'rare') return 'bg-blue-500 text-white';
        if (rarityLower === 'sr' || rarityLower === 'super rare') return 'bg-purple-500 text-white';
        if (rarityLower === 'ur' || rarityLower === 'ultra rare') return 'bg-yellow-500 text-white';
        if (rarityLower === 'scr' || rarityLower === 'secret rare') {
            return 'bg-gradient-to-r from-red-500 via-yellow-500 via-green-500 via-blue-500 to-purple-500 text-white';
        }
        if (rarityLower === 'cbr') return 'bg-[#D2B48C] text-black';
        if (rarityLower === 'pr') return 'bg-[#F5F5F5] text-black border border-gray-300';
        return 'bg-gray-400 text-white';
    };

    const getRarityAbbreviation = (rarity: string) => {
        const rarityLower = rarity.toLowerCase();
        if (rarityLower === 'c' || rarityLower === 'common') return 'C';
        if (rarityLower === 'r' || rarityLower === 'rare') return 'R';
        if (rarityLower === 'sr' || rarityLower === 'super rare') return 'SR';
        if (rarityLower === 'ur' || rarityLower === 'ultra rare') return 'UR';
        if (rarityLower === 'scr' || rarityLower === 'secret rare') return 'SCR';
        if (rarityLower === 'cbr') return 'CBR';
        if (rarityLower === 'pr') return 'PR';
        return rarity.toUpperCase();
    };

    const handleShareBatch = () => {
        const url = window.location.href;
        navigator.clipboard.writeText(url);
        alert(t('auctions.auctionDetail.linkCopied'));
    };

    if (loading) return (
        <div className="container mx-auto px-4 py-8">
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">{t('auctions.loadingBatch')}</p>
                </div>
            </div>
        </div>
    );

    if (!auctions || auctions.length === 0) return (
        <div className="container mx-auto px-4 py-8">
            <div className="bg-white p-6 rounded-lg shadow text-center">
                <p className="text-gray-600 mb-4">{t('auctions.batchNotFound')}</p>
                <Link to="/auctions" className="text-blue-600 hover:underline">
                    {t('auctions.auctionDetail.backToAuctions')}
                </Link>
            </div>
        </div>
    );



    return (
        <div className="container mx-auto px-4 py-8">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <Link to="/auctions" className="text-gray-500 hover:text-blue-600">
                    ← {t('auctions.auctionDetail.backToAuctions')}
                </Link>
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleShareBatch}
                        className="text-blue-600 font-medium hover:underline flex items-center gap-2"
                    >
                        🔗 {t('auctions.auctionDetail.shareAuction')}
                    </button>
                    {facebookLink && (
                        <a
                            href={facebookLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium flex items-center gap-2"
                        >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
                            {t('auctions.verifyOnFacebook')}
                        </a>
                    )}

                    {(isAuthenticated && (currentUser?.id === seller?.id || currentUser?.role === 'ADMIN')) && (
                        <button
                            onClick={handleCancelBatch}
                            className="px-4 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200 font-medium"
                        >
                            {t('auctions.cancelBatch')}
                        </button>
                    )}
                </div>
            </div>

            {/* Grid of Auctions */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {auctions.map(auction => {
                    // Safe image parsing with fallback
                    let image = null;
                    if (auction.imageUrls) {
                        try {
                            const parsed = JSON.parse(auction.imageUrls);
                            if (Array.isArray(parsed) && parsed.length > 0) {
                                image = parsed[0];
                            }
                        } catch (e) { /* ignore */ }
                    }
                    if (!image) {
                        // Fallback to card image
                        image = auction.items?.[0]?.card.imageUrl || auction.card?.imageUrl;
                    }

                    // Prioritize specific card name
                    const card = auction.items?.[0]?.card || auction.card;
                    const title = card?.name || auction.title;
                    const minBid = calculateMinBid(auction);
                    const isLive = auction.status === 'LIVE';

                    return (
                        <div key={auction.id} className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden flex flex-col">
                            {/* Image Area */}
                            <div className="aspect-[3/4] bg-gray-100 relative group">
                                {image ? (
                                    <img src={image} alt={title} className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-4xl">🎴</div>
                                )}
                                <span className={`absolute top-2 left-2 px-2 py-1 text-xs font-bold rounded text-white ${isLive ? 'bg-green-600' : 'bg-gray-500'}`}>
                                    {auction.status}
                                </span>

                                <Link to={`/auctions/${auction.id}`} className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition flex items-center justify-center opacity-0 group-hover:opacity-100">
                                    <span className="bg-white/90 px-3 py-1 rounded-full text-sm font-semibold text-gray-800 shadow">{t('auctions.viewDetails')}</span>
                                </Link>
                            </div>

                            {/* Content */}
                            <div className="p-4 flex-1 flex flex-col">
                                <h3 className="font-semibold text-gray-800 mb-1 truncate" title={title}>{title}</h3>

                                {/* Badges */}
                                {(card?.rarity || card?.language) && (
                                    <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                                        {card.cardNumber && (
                                            <span className="text-[10px] text-gray-500 font-medium bg-gray-100 px-1.5 py-0.5 rounded">
                                                {card.cardNumber}
                                            </span>
                                        )}
                                        {card.rarity && (
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${getRarityColor(card.rarity)}`}>
                                                {getRarityAbbreviation(card.rarity)}
                                            </span>
                                        )}
                                        {card.language && (
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${card.language === 'EN' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                                                {card.language}
                                            </span>
                                        )}
                                    </div>
                                )}

                                <p className="text-sm text-gray-500 mb-3">{formatDistanceToNow(new Date(auction.endTime), { addSuffix: true })}</p>

                                <div className="mt-auto">
                                    <div className="flex justify-between items-end mb-3">
                                        <div>
                                            <p className="text-xs text-gray-500">{t('auctions.currentPrice')}</p>
                                            <p className="text-lg font-bold text-blue-700">{Number(auction.currentPrice || auction.startPrice).toLocaleString()} ฿</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-xs text-gray-500">{t('auctions.minBid')}</p>
                                            <p className="text-sm font-semibold">{minBid.toLocaleString()} ฿</p>
                                        </div>
                                    </div>

                                    {/* Last Bidder Info */}
                                    {(() => {
                                        if (auction.bids && auction.bids.length > 0) {
                                            const lastBid = auction.bids.reduce((prev, current) => (prev.id > current.id) ? prev : current);
                                            // @ts-ignore
                                            const reviews = lastBid.bidder?.receivedReviews || [];
                                            const avgRating = reviews.length > 0
                                                ? reviews.reduce((sum: number, r: any) => sum + r.rating, 0) / reviews.length
                                                : 0;

                                            return (
                                                <div className="mb-2">
                                                    <p className="text-[10px] text-gray-400 mb-1">{t('auctions.lastBidLabel')}</p>
                                                    <div className="flex items-center gap-2 bg-gray-50 p-2 rounded">
                                                        {lastBid.bidder?.profilePicture ? (
                                                            <img src={lastBid.bidder.profilePicture} alt={lastBid.bidder.displayName} className="w-8 h-8 rounded-full object-cover border border-gray-200" />
                                                        ) : (
                                                            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-500">
                                                                {lastBid.bidder?.displayName?.charAt(0).toUpperCase()}
                                                            </div>
                                                        )}
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-1">
                                                                <p className="text-sm font-semibold text-gray-800 truncate max-w-[80px] sm:max-w-[100px]" title={lastBid.bidder?.displayName}>{lastBid.bidder?.displayName}</p>
                                                                {avgRating > 0 && (
                                                                    <div className="flex items-center text-[10px] text-amber-500">
                                                                        <span>★</span>
                                                                        <span className="ml-0.5 font-medium">{avgRating.toFixed(1)}</span>
                                                                        <span className="text-gray-300 ml-0.5">({reviews.length})</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <p className="text-[10px] text-gray-500">
                                                                {formatDistanceToNow(new Date(lastBid.createdAt), { addSuffix: true })}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        } else {
                                            // Empty state to keep alignment
                                            return (
                                                <div className="mb-2">
                                                    <p className="text-[10px] text-transparent mb-1 select-none">placeholder</p>
                                                    <div className="h-[50px] flex items-center justify-center bg-gray-50/50 rounded border border-dashed border-gray-200">
                                                        <p className="text-xs text-gray-400 italic">{t('auctions.noBidsYet')}</p>
                                                    </div>
                                                </div>
                                            );
                                        }
                                    })()}

                                    {/* Inline Bidding */}
                                    {isLive && (
                                        <div className="flex gap-2">
                                            <input
                                                type="number"
                                                min={minBid}
                                                step={auction.minIncrement || 10}
                                                placeholder={String(minBid)}
                                                value={bidAmounts[auction.id] || ''}
                                                onChange={(e) => setBidAmounts(prev => ({ ...prev, [auction.id]: e.target.value }))}
                                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                            />
                                            <button
                                                onClick={() => handlePlaceBid(auction)}
                                                disabled={placingBidOn === auction.id}
                                                className="px-3 py-1 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:bg-gray-300"
                                            >
                                                {t('auctions.bid')}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default AuctionBatchPage;
